// Analysis > MeshCore > Top Talkers (issue #25): the nodes that originated the most packets and the ones that sent
// the most adverts. Every number is counted by the background pass (audit/talkers.py); this file only words it.

export type TalkersWindow = "24h" | "7d";
export const TALKERS_WINDOWS: TalkersWindow[] = ["24h", "7d"];

export interface TalkerRow {
  rank: number;
  node_id: string;
  name: string;
  count: number;
  share: number | null;
  previous: number;
  change: number | null;
}

export interface TalkerBlock {
  total: number;
  /** Packets with a known sender; the share is taken over these. */
  attributed?: number;
  unattributed: number;
  node_count: number;
  rows: TalkerRow[];
}

export interface TalkersPayload {
  window: TalkersWindow;
  generated_at: string;
  window_start: string;
  window_end: string;
  previous_start: string;
  /** End of the newest hour the counts reach; null when nothing is counted yet. */
  data_through: string | null;
  top_count: number;
  talkers: TalkerBlock;
  advertisers: TalkerBlock;
}

export type TalkersResponse = TalkersPayload | { pending: true };

export const TALKERS_TITLE = "Top talkers";
export const ADVERTISERS_TITLE = "Top advertisers";
export const TALKERS_DESC =
  "Nodes ranked by the packets they sent themselves, adverts left out (they are listed below). Each packet counts once, however many observers heard it; packets a node only passed along are not counted. Share is the part of the packets with a known sender.";
export const ADVERTISERS_DESC =
  "Nodes ranked by the adverts they sent themselves. Each advert counts once, however many observers heard it.";
export const NO_ROWS_TEXT = "No packets from a known node in this window.";
export const CHANGE_TIP = "Count in this window minus the count in the window of the same length just before it.";

/** What the Change column compares to, e.g. "vs previous 24 h". */
export function changeLabel(window: TalkersWindow): string {
  return `vs previous ${window === "7d" ? "7 d" : "24 h"}`;
}

export function isPending(r: TalkersResponse | null | undefined): r is { pending: true } {
  return !!r && (r as { pending?: boolean }).pending === true;
}

export function shareText(share: number | null): string {
  if (share === null || share === undefined) return "--";
  const pct = share * 100;
  return `${pct < 10 ? pct.toFixed(1) : Math.round(pct)}%`;
}

/** "+120", "-30", "0", or "New" for a node with nothing in the previous window. */
export function changeText(row: Pick<TalkerRow, "count" | "previous" | "change">): string {
  if (row.previous === 0) return "New";
  const d = row.change ?? row.count - row.previous;
  return d > 0 ? `+${d}` : String(d);
}

export function totalsText(b: TalkerBlock, noun: string): string {
  const base = `${b.total.toLocaleString("en-US")} ${noun} in this window`;
  return b.unattributed > 0 ? `${base}, ${b.unattributed.toLocaleString("en-US")} not attributed to a node` : base;
}

/** The note shown when the counts stop short of the window (the hourly rows lag by two hours or more); otherwise null. */
export function staleNote(p: Pick<TalkersPayload, "window_end" | "data_through">, formatTime: (iso: string) => string): string | null {
  if (!p.data_through) return "No packet counts have been recorded yet.";
  const gapMs = Date.parse(p.window_end) - Date.parse(p.data_through);
  return gapMs >= 2 * 3600 * 1000 ? `Packet counts only reach ${formatTime(p.data_through)}.` : null;
}
